import React, { useState, useEffect } from "react";
import "../../styles/global.css";
import { Logo } from "../../assets/Logo";
import { MenuSearchBar } from "./MenuSearchBar/MenuSearchBar";
import { LocationSelector } from "./LocationSelector/LocationSelector";
import { SearchResultCard } from "./SearchResultCard";
import { AccountControl } from "../Auth/AccountControl";
import { searchMenu, MenuSearchHit } from "../../api/search";

const MTRENDING = [
  "california burrito",
  "fish tacos",
  "ramen",
  "fried chicken sandwich",
  "oysters",
];

/* How many menu items a search shows. */
const MAX_RESULTS = 10;

/* Skeleton rows shown while a search is in flight. */
const SKELETON_ROWS = 4;

/* Per-card stagger delay for the entrance animation. */
function cardDelay(i: number): React.CSSProperties {
  return { "--i": String(i) } as React.CSSProperties;
}

export function LandingPage(): React.ReactElement {
  const [query, setQuery] = useState("");
  const [submitted, setSubmitted] = useState("");
  const [city, setCity] = useState("San Diego");

  const [results, setResults] = useState<MenuSearchHit[]>([]);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState(false);

  const showResults = submitted.trim().length > 0;

  /* Search fires only on an explicit submit (Enter, the search button, or a
   * trending chip), never as the user types — see the setSubmitted calls
   * below. This effect runs the fetch whenever `submitted` changes. */
  useEffect(() => {
    const q = submitted.trim();
    if (!q) {
      setResults([]);
      return;
    }
    let stale = false; // A newer search superseded this one — drop its result.
    setSearching(true);
    setSearchError(false);
    searchMenu(q, MAX_RESULTS)
      .then((hits) => {
        if (!stale) setResults(hits);
      })
      .catch(() => {
        if (!stale) {
          setResults([]);
          setSearchError(true);
        }
      })
      .finally(() => {
        if (!stale) setSearching(false);
      });
    return () => {
      stale = true;
    };
  }, [submitted]);

  return (
    <>
      <header className={showResults ? "topbar" : "topbar topbar-idle"}>
        <LocationSelector value={city} onChange={setCity} />
        <span className="brandname">
          <Logo size={52} />
          Grubwick
        </span>
        <AccountControl variant="topbar" />
      </header>

      <header className={showResults ? "hero" : "hero hero-centered"}>
        <div className="wrap">
          <div className="search-cta">
            {/* Mobile brand: stays docked above the search bar in every state.
               It and the topbar copy are mutually exclusive (each display:none
               at the other's breakpoint), so this is the accessible brand name
               below 900px and the topbar copy is above it — neither is hidden
               from assistive tech. */}
            <span className="brandname hero-brand">
              <Logo size={52} />
              Grubwick
            </span>
            <MenuSearchBar
              value={query}
              onChange={setQuery}
              accent="#0E7C6F"
              placeholder={`Find menu items in ${city}…`}
              onSubmit={(q) => setSubmitted(q)}
              /* Trending dropdown hidden for now — pass trending={MTRENDING}
                 to bring it back. */
            />
          </div>
          <div className="trend-row">
            <span className="lbl">Try a search</span>
            {MTRENDING.map((t) => (
              <button
                key={t}
                className="trend"
                onClick={() => {
                  setQuery(t);
                  setSubmitted(t);
                }}
              >
                {t}
              </button>
            ))}
          </div>
        </div>
      </header>

      {showResults && (
        <section className="results">
          <div className="wrap">
            <div className="results-head">
              <h2>
                <span className="q">{submitted}</span> in {city}
              </h2>
              {!searching && !searchError && results.length > 0 && (
                <span className="count">Top matches</span>
              )}
            </div>
            {searching ? (
              <div className="mcard-grid" aria-busy="true">
                <span className="sr-only">Searching menus…</span>
                {Array.from({ length: SKELETON_ROWS }, (_, i) => (
                  <div className="mcard skel" key={i} style={cardDelay(i)}>
                    <div className="mcard-top">
                      <span className="skel-line" style={{ width: 88 }} />
                    </div>
                    <span className="skel-line" style={{ width: "70%", height: 16 }} />
                    <span className="skel-line" style={{ width: "92%" }} />
                    <span className="skel-line" style={{ width: "60%" }} />
                    <div className="mcard-foot">
                      <span className="skel-line" style={{ width: 52 }} />
                    </div>
                  </div>
                ))}
              </div>
            ) : searchError ? (
              <div className="results-note">
                Search isn&rsquo;t available right now — please try again.
              </div>
            ) : results.length ? (
              <div className="mcard-grid">
                {results.map((it, i) => (
                  <SearchResultCard key={it.id} hit={it} index={i} />
                ))}
              </div>
            ) : (
              <div className="results-note">
                No menu items match yet — try &ldquo;tacos&rdquo;,
                &ldquo;ramen&rdquo;, or &ldquo;fried chicken&rdquo;.
              </div>
            )}
          </div>
        </section>
      )}

      <footer>
        <div className="wrap foot-in">
          <Logo size={20} /> &copy; 2026 Grubwick · The menu search engine
        </div>
      </footer>
    </>
  );
}
